import { useMemo, useState } from 'react';
import { CalcStatus, GetContractCondition } from '../../../types/contracts';
import Conditions from '../components/Conditions';
import { useGetContractsQuery } from '../../../api/contractApi';
import Pager from '../components/Pager';
import styles from './styles.module.css';
import ContractsTable from '../components/ContractsTable';
import Button from '../../../components/common/Button';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useOrderCalcAllocationMutation } from '../../../api/carAllocationApi';
import FooterControl from '../../../components/common/FooterControl';
import PolicyRequired from '../../../components/common/PolicyRequired';
import LastCalculatedAt from '../../../components/common/LastCalculatedAt';
import HeaderLayout from '../../../components/HeaderLayout';
import FooterLayout, {
  FooterLayoutCenterize,
} from '../../../components/FooterLayout';
import { logger } from '../../../services/logger';
import NoResultFound from '../../../components/common/NoResultFound';
import { format } from 'date-fns';

/**
 * 「計算してください」のテキストを表示する計算状態
 * @see https://fenrir-inc.backlog.com/view/MRBN_UR_INHOUSE-603#comment-320441981
 */
const showCalcPromtStatuses: CalcStatus[] = [
  'uncalced',
  'partComplete',
  'updateContract',
];

const initConditions: GetContractCondition = {
  contractNo: null,
  companyName: '',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: '',
  calcStatus: 'uncalced',
  goesStraight: null,
  wasteItemIds: [],
  page: 1,
};
function ContractList() {
  const [conditions, updateConditions] = useState<GetContractCondition>({
    ...initConditions,
    limit: 20,
  });
  const onChangePerPage = (perPage: number) => {
    updateConditions((prev) => ({
      ...prev,
      page: 1,
      limit: perPage,
    }));
  };
  const onPage = (page: number) => {
    updateConditions((prev) => ({
      ...prev,
      page,
    }));
  };
  const {
    data: { contracts, total, lastCalculatedAt } = {
      contracts: [],
      total: 0,
      lastCalculatedAt: null,
    },
  } = useGetContractsQuery(conditions);
  const isCarAllocationIncomplete = useMemo(
    () => contracts.some((c) => showCalcPromtStatuses.includes(c.calcStatus)),
    [contracts]
  );
  const hasUpdateContract = useMemo(
    () => contracts.some((c) => c.calcStatus === 'updateContract'),
    [contracts]
  );

  const [order] = useOrderCalcAllocationMutation();
  const [isToastPopUp, setIsToastPopUp] = useState(false);

  return (
    <HeaderLayout
      title={
        <>
          <div>受注入力/配車計算</div>
          <div className={styles.notation}>
            {lastCalculatedAt && (
              <LastCalculatedAt value={new Date(lastCalculatedAt)} />
            )}
          </div>
        </>
      }
      controls={
        <PolicyRequired policy={19}>
          <Link to="new" className={styles.newContractLink}>
            <FontAwesomeIcon icon={faPlus} className={styles.newContractIcon} />
            受注入力
          </Link>
        </PolicyRequired>
      }
    >
      <FooterLayout
        main={
          <>
            <PolicyRequired policy={22}>
              <Conditions
                conditions={conditions}
                initConditions={initConditions}
                updateConditions={updateConditions}
              ></Conditions>
            </PolicyRequired>
            {contracts.length > 0 ? (
              <>
                <Pager
                  page={conditions.page || 1}
                  perPage={conditions.limit || 20}
                  total={total}
                  onPage={onPage}
                  onChangePerPage={onChangePerPage}
                />
                <div className={styles.content}>
                  <ContractsTable contracts={contracts} />
                </div>
                {contracts.length > 0 && (
                  <Pager
                    page={conditions.page || 1}
                    perPage={conditions.limit || 20}
                    total={total}
                    onPage={onPage}
                    onChangePerPage={onChangePerPage}
                    isToastPopUp={isToastPopUp}
                  />
                )}
              </>
            ) : (
              <FooterLayoutCenterize>
                <div className={styles.noResultFound}>
                  <NoResultFound />
                </div>
              </FooterLayoutCenterize>
            )}
          </>
        }
        footer={
          <PolicyRequired policy={29}>
            <div className={styles.footer}>
              {isCarAllocationIncomplete && (
                <div className={styles.carAllocateError}>
                  {hasUpdateContract
                    ? '条件を変更した案件があるため、計算してください'
                    : '未配車の案件があるため、計算してください'}
                </div>
              )}
              <FooterControl setIsNeedBottomMargin={(v) => setIsToastPopUp(v)}>
                <div className={styles.calculateButton}>
                  <Button
                    onClick={() => {
                      order().unwrap().catch(logger.error);
                    }}
                    variants={['fit']}
                    disabled={isToastPopUp}
                  >
                    配車を計算
                  </Button>
                </div>
              </FooterControl>
            </div>
          </PolicyRequired>
        }
      />
    </HeaderLayout>
  );
}

export default ContractList;
